import React, {useState, useCallback} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {withRouter, useRouteUnmountEffect} from '@computerrock/formation-router';
import {efServiceCaseTypes} from '@ace-de/eua-entity-types';
import {useStyles, ContentBlock, ContentItem} from '@ace-de/ui-components';
import * as serviceCaseSelectors from './serviceCaseSelectors';
import DocumentUploadPanel from './ui-elements/DocumentUploadPanel';
import MemberInfoPanel from './ui-elements/MemberInfoPanel';
import VehicleInfoPanel from './ui-elements/VehicleInfoPanel';
import DamageLocationInfoPanel from './ui-elements/DamageLocationInfoPanel';
import SCBasicDataTowingDestinationInfoPanel from '../service-assignments-vehicle/ui-elements/SCBasicDataTowingDestinationInfoPanel';
import ContactDetailsPanel from './ui-elements/ContactDetailsPanel';
import ServiceCaseInfoPanel from './ui-elements/ServiceCaseInfoPanel';
import * as serviceCaseActionTypes from './serviceCaseActionTypes';
import config from '../config';
import DamageDescriptionInfoPanel from './ui-elements/DamageDescriptionInfoPanel';
import TrailerInfoPanel from '../service-assignments/ui-elements/TrailerInfoPanel';

const SCBasicDataTab = props => {
    const {serviceCase, updateSCBasicData, initiateEditServiceCaseVehicle} = props;
    const {latestServiceAssignmentWithTowingDestination, initialServiceAssignment} = props;
    const {cx} = useStyles();
    const [externalCaseId, setExternalCaseId] = useState(serviceCase.externalCaseId || '');
    const [fallbackSystemId, setFallbackSystemId] = useState(serviceCase?.fallbackSystemId || '');
    const [email, setEmail] = useState(serviceCase?.member?.contactDetails?.email || '');
    const [callbackPhoneNo, setCallbackPhoneNo] = useState(serviceCase?.callbackPhoneNo || '');
    const [secondCallbackPhoneNo, setSecondCallbackPhoneNo] = useState(
        initialServiceAssignment?.secondCallbackPhoneNo || '',
    );

    const handleOnChange = (key, value) => {
        switch (key) {
            case 'externalCaseId': {
                setExternalCaseId(value);
                break;
            }

            case 'fallbackSystemId': {
                setFallbackSystemId(value);
                break;
            }

            case 'email': {
                setEmail(value);
                break;
            }

            case 'callbackPhoneNo': {
                setCallbackPhoneNo(value);
                break;
            }

            case 'secondCallbackPhoneNo': {
                setSecondCallbackPhoneNo(value);
                break;
            }

            default: {
                // no-op
            }
        }
    };

    const handleTabChange = useCallback(serviceCaseData => {
        if (!serviceCase || !initialServiceAssignment) return;

        updateSCBasicData({
            caseType: serviceCase.caseType,
            serviceCaseId: serviceCase.id,
            serviceCaseData: {
                externalCaseId: serviceCaseData.externalCaseId,
                fallbackSystemId: serviceCaseData.fallbackSystemId,
                member: {
                    contactDetails: {
                        email: serviceCaseData.email,
                    },
                },
                ...(serviceCaseData.callbackPhoneNo && {
                    callbackPhoneNo: serviceCaseData.callbackPhoneNo,
                    unknownCallbackPhoneNo: false,
                }),
            },
            serviceAssignmentData: {
                assignmentType: initialServiceAssignment?.assignmentType,
                serviceAssignmentLineNo: initialServiceAssignment?.lineNo,
                ...(serviceCaseData?.callbackPhoneNo && {
                    callbackPhoneNo: serviceCaseData.callbackPhoneNo,
                }),
                secondCallbackPhoneNo: serviceCaseData?.secondCallbackPhoneNo,
            },
        });
    }, [serviceCase, initialServiceAssignment, updateSCBasicData]);

    useRouteUnmountEffect(({completeRouteUnmountSideEffect}) => {
        if (externalCaseId === serviceCase.externalCaseId
            && fallbackSystemId === serviceCase.fallbackSystemId
            && email === serviceCase.member?.contactDetails?.email
            && callbackPhoneNo === serviceCase.callbackPhoneNo
            && secondCallbackPhoneNo === initialServiceAssignment?.secondCallbackPhoneNo
        ) {
            completeRouteUnmountSideEffect({
                caller: serviceCaseActionTypes.INITIATE_UPDATE_SC_BASIC_DATA_FLOW,
            });
            return;
        }

        handleTabChange({externalCaseId, fallbackSystemId, email, callbackPhoneNo, secondCallbackPhoneNo});
    }, [
        externalCaseId,
        serviceCase.externalCaseId,
        fallbackSystemId,
        serviceCase.fallbackSystemId,
        email,
        serviceCase.member?.contactDetails?.email,
        callbackPhoneNo,
        serviceCase.callbackPhoneNo,
        secondCallbackPhoneNo,
        initialServiceAssignment?.secondCallbackPhoneNo,
        handleTabChange,
    ]);

    // if no case don't render
    if (!serviceCase) return null;

    const {member, vehicle, damage} = serviceCase;

    const hideVehicleInfo = (serviceCase.caseType === efServiceCaseTypes.PERSON && !vehicle)
        || serviceCase.caseType === efServiceCaseTypes.HOUSEHOLD;

    return (
        <ContentBlock
            className={cx([
                'global!ace-u-height--full',
                'global!ace-u-max-height--full',
                'global!ace-u-flex--align-stretch',
            ])}
        >
            <ContentItem className={cx('ace-c-content-item--span-9')}>
                {member && (
                    <MemberInfoPanel
                        member={member}
                        serviceCase={serviceCase}
                        email={email}
                        callbackPhoneNo={callbackPhoneNo}
                        secondCallbackPhoneNo={secondCallbackPhoneNo}
                        handleOnChange={handleOnChange}
                    />
                )}
                <ServiceCaseInfoPanel
                    serviceCase={serviceCase}
                    externalCaseId={externalCaseId}
                    fallbackSystemId={fallbackSystemId}
                    handleOnChange={handleOnChange}
                />
                {hideVehicleInfo ? null : (
                    <VehicleInfoPanel
                        handleOnEdit={() => initiateEditServiceCaseVehicle({vehicleId: vehicle.vehicleId})}
                    />
                )}
                {!!serviceCase?.trailer && (
                    <TrailerInfoPanel />
                )}
                {serviceCase.caseType === efServiceCaseTypes.VEHICLE && (
                    <SCBasicDataTowingDestinationInfoPanel
                        key={latestServiceAssignmentWithTowingDestination}
                        latestServiceAssignmentWithTowingDestination={latestServiceAssignmentWithTowingDestination}
                        serviceCase={serviceCase}
                    />
                )}
            </ContentItem>
            <ContentItem className={cx('ace-c-content-item--span-3')}>
                {damage && (
                    <DamageLocationInfoPanel damage={damage} />
                )}
                <DamageDescriptionInfoPanel />
                {!config.IS_FALLBACK_SYSTEM && (
                    <DocumentUploadPanel />
                )}
                <ContactDetailsPanel />
            </ContentItem>
        </ContentBlock>
    );
};

SCBasicDataTab.propTypes = {
    serviceCase: PropTypes.object,
    latestServiceAssignmentWithTowingDestination: PropTypes.object,
    initialServiceAssignment: PropTypes.object,
    updateSCBasicData: PropTypes.func.isRequired,
    initiateEditServiceCaseVehicle: PropTypes.func.isRequired,
};

SCBasicDataTab.defaultProps = {
    serviceCase: null,
    latestServiceAssignmentWithTowingDestination: null,
    initialServiceAssignment: null,
};

const mapStateToProps = (state, props) => {
    const serviceCaseSelector = serviceCaseSelectors.createServiceCaseSelector();
    const serviceAssignmentWithTowingDestinationSelector = serviceCaseSelectors.createServiceAssignmentWithTowingDestinationSelector(); // eslint-disable-line max-len
    const getInitialAssignment = serviceCaseSelectors.createInitialServiceAssignmentSelector();
    return {
        serviceCase: serviceCaseSelector(state, props),
        latestServiceAssignmentWithTowingDestination: serviceAssignmentWithTowingDestinationSelector(state, props),
        initialServiceAssignment: getInitialAssignment(state, props),
    };
};

const mapDispatchToProps = dispatch => ({
    updateSCBasicData: payload => dispatch({
        type: serviceCaseActionTypes.INITIATE_UPDATE_SC_BASIC_DATA_FLOW,
        payload,
    }),
    initiateEditServiceCaseVehicle: payload => dispatch({
        type: serviceCaseActionTypes.INITIATE_EDIT_SERVICE_CASE_VEHICLE_FLOW,
        payload,
    }),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SCBasicDataTab));
